import { useEffect, useRef, useState } from "react";

import { cn, platformProps } from "../../lib";
import { useScrollOverflow } from "../../lib/scroll-overflow";
import type { SheetScrollBodyProps } from "./types";

/**
 * A content-sized sheet's Body (web). It takes its content's height until the
 * sheet reaches its maximum height, then shrinks and scrolls; header and footer
 * stay in place. While the content overflows it carries `data-vaul-no-drag`, so
 * a drag inside it scrolls instead of moving the sheet.
 */
export const SheetScrollBody = ({
  className,
  testID,
  children,
  onOverflowChange,
}: SheetScrollBodyProps) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const overflowing = useScrollOverflow(viewportRef, onOverflowChange);
  const [noDrag, setNoDrag] = useState(false);
  // Vaul checks the attribute while it decides whether a drag moves the sheet,
  // so a change waits until every pointer that went down in the Body is up
  const activePointers = useRef(new Set<number>());
  const pending = useRef<boolean | null>(null);

  useEffect(() => {
    if (activePointers.current.size > 0) pending.current = overflowing;
    else setNoDrag(overflowing);
  }, [overflowing]);

  useEffect(() => {
    const release = (e: PointerEvent) => {
      if (!activePointers.current.delete(e.pointerId)) return;
      if (activePointers.current.size > 0 || pending.current === null) return;
      setNoDrag(pending.current);
      pending.current = null;
    };
    window.addEventListener("pointerup", release);
    window.addEventListener("pointercancel", release);
    return () => {
      window.removeEventListener("pointerup", release);
      window.removeEventListener("pointercancel", release);
    };
  }, []);

  return (
    <div
      ref={viewportRef}
      className="min-h-0 shrink overflow-y-auto"
      data-vaul-no-drag={noDrag ? "" : undefined}
      onPointerDown={(e) => {
        activePointers.current.add(e.pointerId);
      }}
      {...platformProps({ testID, slot: "bottom-sheet-body" })}
    >
      <div className={cn("flex flex-col gap-2 p-4", className)}>{children}</div>
    </div>
  );
};
